import { combineReducers } from 'redux';

import { USER_LOGOUT } from './ducks/auth.duck';

/**
 * Placeholder for state keys that exist in the preloaded state but whose duck has not been
 * evaluated yet (SSR ships e.g. `ListingPage` before the client has loaded that chunk).
 *
 * Registering an identity reducer — instead of spreading unknown keys back in around
 * `combineReducers` — keeps two properties we need:
 *  1. no "Unexpected key" warning from redux in development, and
 *  2. reference stability: `combineReducers` returns the *same* state object when nothing changed,
 *     which react-redux relies on to skip re-renders.
 */
const passthroughReducer = (state = null) => state;

/**
 * Hands `undefined` to the reducers of `keys` so that redux initialises those slices from their
 * own initial state on the next `combined()` call. Returns `state` untouched when there is nothing
 * to reset, so reference stability (and with it react-redux's re-render skipping) is preserved.
 *
 * @param {Object|undefined} state current root state
 * @param {Set<string>} keys slice keys to re-initialise
 * @returns {Object|undefined} state, or a shallow copy with those keys cleared
 */
const clearSlices = (state, keys) => {
  if (!state || keys.size === 0) {
    return state;
  }
  let next = state;
  keys.forEach((key) => {
    // `null` is what `passthroughReducer` produced while the key had no duck; a real slice value
    // must never be discarded.
    if (next[key] === null) {
      if (next === state) {
        next = { ...state };
      }
      next[key] = undefined;
    }
  });
  return next;
};

/**
 * Creates a reducer that can grow new state slices at runtime.
 *
 * @param {Object} staticReducers reducers always present in the store (the global ducks)
 * @param {Object} [registry] optional reducer registry, `{ getReducers, getVersion }`. When given,
 *   newly registered reducers are pulled in lazily on the next action — this is what makes
 *   per-request SSR stores correct without them having to subscribe to the registry.
 */
export const createReducerManager = (staticReducers = {}, registry = null) => {
  const reducers = { ...staticReducers };
  let combined = combineReducers(reducers);
  // -1 so the very first action (redux's own INIT) always syncs.
  let syncedVersion = -1;
  let scannedState = null;
  // Keys currently served by `passthroughReducer`.
  const placeholders = new Set();
  // Keys whose placeholder was just replaced by the duck's real reducer. Their state may still
  // hold the placeholder's `null` — see `promote`.
  const promoted = new Set();

  const rebuild = () => {
    combined = combineReducers(reducers);
    scannedState = null;
  };

  /**
   * Records that `key` is no longer a placeholder. Its state must be re-initialised once, because
   * a placeholder that outlived a USER_LOGOUT holds `null` (the reset handed `passthroughReducer`
   * its `state = null` default), and redux's `state = initialState` default parameter only fills
   * in for `undefined`. Without this the duck's real reducer keeps returning `null` for the rest
   * of the session and every consumer crashes on `state.<Page>.<field>`
   * (VANSITE-FRONTEND-4QX/-4QY/-4QN).
   *
   * @param {string} key state key that just got its real reducer
   */
  const promote = (key) => {
    if (placeholders.delete(key)) {
      promoted.add(key);
    }
  };

  const add = (key, reducer) => {
    if (!key || typeof reducer !== 'function' || reducers[key] === reducer) {
      return false;
    }
    reducers[key] = reducer;
    if (reducer !== passthroughReducer) {
      promote(key);
    }
    rebuild();
    return true;
  };

  const syncFromRegistry = () => {
    if (!registry || registry.getVersion() === syncedVersion) {
      return;
    }
    syncedVersion = registry.getVersion();
    const registered = registry.getReducers();
    const changed = Object.keys(registered).reduce((acc, key) => {
      if (reducers[key] === registered[key]) {
        return acc;
      }
      reducers[key] = registered[key];
      promote(key);
      return true;
    }, false);
    if (changed) {
      rebuild();
    }
  };

  // Keys can only appear from a preloaded state, so scanning is skipped as long as the state
  // object identity is unchanged.
  const ensurePlaceholders = (state) => {
    if (!state || state === scannedState) {
      return;
    }
    const changed = Object.keys(state).reduce((acc, key) => {
      if (reducers[key]) {
        return acc;
      }
      reducers[key] = passthroughReducer;
      placeholders.add(key);
      return true;
    }, false);
    if (changed) {
      rebuild();
    }
    scannedState = state;
  };

  const reduce = (state, action) => {
    syncFromRegistry();

    const isLogout = action.type === USER_LOGOUT;

    // Clear sessionStorage when logging out.
    if (isLogout && typeof window !== 'undefined' && !!window.sessionStorage) {
      window.sessionStorage.clear();
    }

    // Resetting to undefined makes every reducer — including the injected page slices — fall back
    // to its initial state.
    const appState = isLogout ? undefined : state;
    ensurePlaceholders(appState);

    const initialisedState = clearSlices(appState, promoted);
    promoted.clear();

    return combined(initialisedState, action);
  };

  return {
    add,
    getReducerMap: () => ({ ...reducers }),
    reduce,
  };
};

export default createReducerManager;
